import { useState } from 'react';
import { AlertTriangle, Check, Loader2, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { useCustomerGroup } from '@/hooks/admin/useCustomerGroup';
import {
  useDeleteEdiAlias,
  useEdiAliasCatalogSearch,
  useEdiAliases,
  useEdiSettings,
  useSaveEdiAlias,
  type EdiCatalogItem,
} from '@/hooks/admin/useEdi';

const inputCls =
  'rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink ' +
  'focus:outline-none focus:ring-1 focus:ring-brix-primary';

/**
 * Item crosswalk: the chain's own item codes and descriptions → our catalog.
 *
 * The matcher tries a learned mapping FIRST, then an exact SKU, then a
 * unique catalog search. Chains whose PO carries our SKU (The Melt's "Vendor
 * Item #" column) resolve on the SKU rung with no mapping at all — this panel
 * is for the rest: their internal part numbers, and their wording for a
 * product we call something else ("BAG IN BOX Cola" → Hangar 25 Cola).
 *
 * Mappings are learned automatically every time a PO is submitted from the
 * review queue. This is where you see them, fix a wrong one, or seed a chain
 * before its first PO arrives.
 */
export default function EdiCrosswalkPanel({ customerId }: { customerId: string }) {
  // Only meaningful for a customer that actually receives POs by email.
  const { data: settingsData } = useEdiSettings(customerId);
  // One crosswalk per chain, on the gateway — a member store has none of its
  // own, so the panel does not belong on its page at all.
  const { data: groupInfo } = useCustomerGroup(customerId);
  const isMemberStore = !!groupInfo?.member_of;
  const ediOn = !!settingsData?.settings && !isMemberStore;
  const { data, isLoading, error } = useEdiAliases(customerId, ediOn);
  const saveAlias = useSaveEdiAlias(customerId);
  const deleteAlias = useDeleteEdiAlias(customerId);

  const [alias, setAlias] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<EdiCatalogItem | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const catalog = useEdiAliasCatalogSearch(search);
  const aliases = data?.aliases ?? [];
  const staleCount = aliases.filter((a) => a.stale).length;

  const add = async () => {
    setMsg(null);
    if (!alias.trim()) {
      setMsg({ kind: 'err', text: 'Enter the code or wording that appears on their PO.' });
      return;
    }
    if (!picked) {
      setMsg({ kind: 'err', text: 'Pick the catalog item it maps to.' });
      return;
    }
    try {
      await saveAlias.mutateAsync({ alias: alias.trim(), catalog_item_id: picked.id });
      setMsg({ kind: 'ok', text: `“${alias.trim()}” now maps to ${picked.name}.` });
      setAlias('');
      setSearch('');
      setPicked(null);
    } catch (err) {
      setMsg({ kind: 'err', text: err instanceof Error ? err.message : 'Could not save that mapping.' });
    }
  };

  if (!ediOn) return null;

  return (
    <CollapsibleCard
      title="Item crosswalk"
      storageKey={`crosswalk-${customerId}`}
      defaultOpen={false}
      tone={staleCount > 0 ? 'warn' : 'default'}
      summary={`${aliases.length} mapping${aliases.length === 1 ? '' : 's'}${staleCount > 0 ? ` · ${staleCount} need attention` : ''}`}
      desc={
        <>
          Their item code or wording → our catalog item. The matcher checks these first, then
          an exact SKU, then a unique catalog search &mdash; so a chain whose PO already carries
          our SKU needs nothing here. Every reviewed submit adds what it learned.
        </>
      }
    >

      {/* Add / re-point */}
      <div className="rounded-lg border border-brix-border/70 bg-brix-bg/40 px-3 py-3 space-y-2">
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              On their PO
            </label>
            <input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="e.g. BAG IN BOX Cola  ·  or their part #"
              className={cn(inputCls, 'w-full mt-1')}
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Our catalog item
            </label>
            {picked ? (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-brix-ink truncate">
                  {picked.sku ? <span className="font-mono">{picked.sku}</span> : null} {picked.name}
                </span>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-[11px] text-brix-muted underline underline-offset-2"
                >
                  change
                </button>
              </div>
            ) : (
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search catalog by name or SKU"
                className={cn(inputCls, 'w-full mt-1')}
              />
            )}
          </div>
        </div>

        {!picked && search.trim().length >= 2 ? (
          <div className="max-h-40 overflow-y-auto rounded-lg border border-brix-border/70 divide-y divide-brix-border/50">
            {catalog.isLoading ? (
              <p className="px-2.5 py-2 text-[11px] text-brix-muted">Searching&hellip;</p>
            ) : (catalog.data?.catalog ?? []).length === 0 ? (
              <p className="px-2.5 py-2 text-[11px] text-brix-muted">No orderable item matches that.</p>
            ) : (
              (catalog.data?.catalog ?? []).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setPicked(item)}
                  className="w-full text-left px-2.5 py-1.5 text-xs text-brix-ink hover:bg-brix-bg/60"
                >
                  {item.sku ? <span className="font-mono text-brix-muted">{item.sku}</span> : null}{' '}
                  {item.name}
                </button>
              ))
            )}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void add()}
            disabled={saveAlias.isPending}
            className="rounded-lg bg-brix-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1"
          >
            {saveAlias.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Plus size={12} />
            )}
            Add mapping
          </button>
          {msg ? (
            <span
              className={cn(
                'text-[12px] inline-flex items-center gap-1',
                msg.kind === 'ok' ? 'text-emerald-600' : 'text-red-600',
              )}
            >
              {msg.kind === 'ok' ? <Check size={12} /> : null}
              {msg.text}
            </span>
          ) : null}
        </div>
      </div>

      {/* Existing mappings */}
      {isLoading ? (
        <p className="text-xs text-brix-muted flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading crosswalk&hellip;
        </p>
      ) : error ? (
        <p className="text-xs text-red-500">
          {error instanceof Error ? error.message : 'Could not load the crosswalk.'}
        </p>
      ) : aliases.length === 0 ? (
        <p className="text-xs text-brix-muted">
          Nothing mapped yet. That is fine when their PO carries our SKU &mdash; the first reviewed
          submit will fill this in.
        </p>
      ) : (
        <ul className="divide-y divide-brix-border/60 max-h-80 overflow-y-auto">
          {aliases.map((a) => (
            <li key={a.id} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] text-brix-ink truncate">
                  <span className="font-mono text-brix-muted">{a.alias_norm}</span>
                  <span className="text-brix-muted"> → </span>
                  {a.item ? (
                    <>
                      {a.item.sku ? <span className="font-mono">{a.item.sku}</span> : null} {a.item.name}
                    </>
                  ) : (
                    <span className="text-red-500">item no longer in the catalog</span>
                  )}
                </p>
                <p className="text-[11px] text-brix-muted">
                  {a.created_from === 'manual' ? 'Added by hand' : 'Learned from a reviewed PO'}
                  {a.created_by ? ` · ${a.created_by}` : ''}
                </p>
                {a.stale ? (
                  <p className="text-[11px] text-amber-600 inline-flex items-center gap-1 mt-0.5">
                    <AlertTriangle size={11} />
                    Points at an item that is retired or not orderable &mdash; re-point it.
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                title="Remove this mapping"
                disabled={deleteAlias.isPending}
                onClick={() => {
                  if (window.confirm(`Remove the mapping for “${a.alias_norm}”?`)) {
                    void deleteAlias.mutateAsync(a.id);
                  }
                }}
                className="text-brix-muted hover:text-red-600 shrink-0 p-1"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}
